import { action, observable, runInAction } from 'mobx';

import activitiesClient, { ActivitiesConnection } from '../clients/activities/ActivitiesClient';
import ggClient, { ActivityConnection } from '../clients/activities/GGClient';
import { ActivityItem } from '../clients/activities/model';

function transformItems(activity: ActivityConnection = {}): ActivityItem[] {
  if (!activity.nodes) {
    return [];
  }

  return activity.nodes.map(item => {
    const { containers, contributors, eventType, object, timestamp } = item;
    return {
      container: containers ? containers.slice(containers.length - 1)[0] : { name: '' },
      contributors: contributors
        ? contributors.map(contributor => {
            const { accountId, name, picture } = contributor.profile;
            return {
              accountID: accountId,
              name,
              picture,
            };
          })
        : [],
      eventType,
      object,
      timestamp,
    };
  });
}

function transformActivitiesItems(activities: ActivitiesConnection = {}): ActivityItem[] {
  if (!activities.nodes) {
    return [];
  }

  return activities.nodes.map(item => {
    const { object, timestamp } = item;
    const { cloudId, events, extension, iconUrl, id, name, product, url } = object;
    const eventType = events[0].eventType;
    const issueKey = extension && extension.issueKey;

    return {
      container: object.containers ? object.containers.slice(object.containers.length - 1)[0] : { name: '' },
      contributors: object.contributors
        ? object.contributors.map(contributor => {
            const { accountId, picture } = contributor.profile;
            return {
              accountID: accountId,
              name: contributor.profile.name,
              picture,
            };
          })
        : [],
      eventType,
      object: {
        cloudID: cloudId,
        iconURL: iconUrl,
        id,
        name: issueKey ? `${issueKey} ${name}` : name,
        product,
        url,
      },
      timestamp,
    };
  });
}

interface ActivitiesData {
  views: ActivityItem[];
  works: ActivityItem[];
}

async function getMyActivities(): Promise<ActivitiesData> {
  const { data = { myActivities: { workedOn: {}, viewed: {} } } } = await ggClient.getMyActivities();

  const works = transformItems(data.myActivities.workedOn);
  const views = transformItems(data.myActivities.viewed);

  return { views, works };
}

async function getActivities(): Promise<ActivitiesData> {
  const { data = { activities: { workedOn: {}, myViewed: {} } } } = await activitiesClient.getActivities();

  const works = transformActivitiesItems(data.activities.workedOn);
  const views = transformActivitiesItems(data.activities.myViewed);

  return { views, works };
}

export default class ActivityStore {
  @observable hasLoaded: boolean = false;
  @observable views: ActivityItem[] = [];
  @observable works: ActivityItem[] = [];

  @action
  async fetchActivity(useNewAPI: boolean) {
    try {
      let activities: ActivitiesData;

      if (useNewAPI) {
        activities = await getActivities();
      } else {
        activities = await getMyActivities();
      }

      runInAction(() => {
        this.views = activities.views;
        this.works = activities.works;
        this.hasLoaded = true;
      });
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.error('error when fetching from worklens', e);
    }
  }
}
